if (!customElements.get('media-gallery')) {
  const THUMBNAIL_PAGE_SIZES = [
    ['(min-width: 990px)', 6],
    ['(min-width: 750px)', 4],
    ['(min-width: 480px)', 3],
    ['(min-width: 0px)', 2],
  ];
  const THUMBNAIL_DRAG_THRESHOLD = 5;
  const THUMBNAIL_VELOCITY_WINDOW = 110;
  const THUMBNAIL_MAX_VELOCITY = 3600;
  const THUMBNAIL_DECAY = 3.4;
  const THUMBNAIL_STOP_VELOCITY = 3;

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.thumbnailPageQueries = THUMBNAIL_PAGE_SIZES.map(([query, step]) => ({
          media: window.matchMedia(query),
          step,
        }));
        this.updateThumbnailPageSize = this.updateThumbnailPageSize.bind(this);
        this.onThumbnailPointerDown = this.onThumbnailPointerDown.bind(this);
        this.onThumbnailPointerMove = this.onThumbnailPointerMove.bind(this);
        this.onThumbnailPointerUp = this.onThumbnailPointerUp.bind(this);
        this.onThumbnailPointerCancel = this.onThumbnailPointerCancel.bind(this);
        this.onThumbnailPointerCaptureLost = this.onThumbnailPointerCaptureLost.bind(this);
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onThumbnailWheel = this.stopThumbnailInertia.bind(this);
        this.stepThumbnailInertia = this.stepThumbnailInertia.bind(this);
        this.preventThumbnailNativeDrag = this.preventThumbnailNativeDrag.bind(this);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.syncThumbnailVisibility();
        if (!this.thumbnailPageQueries) return;
        this.thumbnailPageQueries.forEach(({ media }) => media.addEventListener('change', this.updateThumbnailPageSize));
        this.updateThumbnailPageSize();
        this.thumbnailRail = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('ul');
        this.thumbnailRail?.addEventListener('pointerdown', this.onThumbnailPointerDown);
        this.thumbnailRail?.addEventListener('pointermove', this.onThumbnailPointerMove);
        this.thumbnailRail?.addEventListener('pointerup', this.onThumbnailPointerUp);
        this.thumbnailRail?.addEventListener('pointercancel', this.onThumbnailPointerCancel);
        this.thumbnailRail?.addEventListener('lostpointercapture', this.onThumbnailPointerCaptureLost);
        this.thumbnailRail?.addEventListener('click', this.onThumbnailClick, true);
        this.thumbnailRail?.addEventListener('wheel', this.onThumbnailWheel, { passive: true });
        this.thumbnailRail?.addEventListener('dragstart', this.preventThumbnailNativeDrag);
      }

      syncThumbnailVisibility() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        const viewerMediaIds = new Set(
          Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).map(({ dataset }) => dataset.mediaId),
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const isVisible = viewerMediaIds.has(thumbnail.dataset.target);
          thumbnail.hidden = !isVisible;
          thumbnail.classList.toggle('thumbnail-list_item--filtered', !isVisible);
          if (!isVisible) thumbnail.querySelector('button')?.removeAttribute('aria-current');
        });

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeThumbnail = activeMedia
          ? this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`)
          : null;
        if (!activeThumbnail || activeThumbnail.hidden) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((button) => button.removeAttribute('aria-current'));
        activeThumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
      }

      disconnectedCallback() {
        this.activeMediaRequest = (this.activeMediaRequest || 0) + 1;
        this.clearPreviewLoading();
        this.elements.viewer.style.removeProperty('block-size');
        this.thumbnailPageQueries?.forEach(({ media }) =>
          media.removeEventListener('change', this.updateThumbnailPageSize)
        );
        this.stopThumbnailInertia();
        this.finishThumbnailDrag(null, false);
        this.thumbnailRail?.removeEventListener('pointerdown', this.onThumbnailPointerDown);
        this.thumbnailRail?.removeEventListener('pointermove', this.onThumbnailPointerMove);
        this.thumbnailRail?.removeEventListener('pointerup', this.onThumbnailPointerUp);
        this.thumbnailRail?.removeEventListener('pointercancel', this.onThumbnailPointerCancel);
        this.thumbnailRail?.removeEventListener('lostpointercapture', this.onThumbnailPointerCaptureLost);
        this.thumbnailRail?.removeEventListener('click', this.onThumbnailClick, true);
        this.thumbnailRail?.removeEventListener('wheel', this.onThumbnailWheel);
        this.thumbnailRail?.removeEventListener('dragstart', this.preventThumbnailNativeDrag);
        this.clearThumbnailClickSuppression();
      }

      onThumbnailPointerDown(event) {
        this.stopThumbnailInertia();
        if (event.button !== 0 || event.isPrimary === false || !['mouse', 'pen'].includes(event.pointerType)) return;

        this.clearThumbnailClickSuppression();
        this.thumbnailDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: this.thumbnailRail.scrollLeft,
          moved: false,
          velocityHistory: [],
        };
      }

      onThumbnailPointerMove(event) {
        if (!this.thumbnailDrag || event.pointerId !== this.thumbnailDrag.pointerId) return;

        const distance = event.clientX - this.thumbnailDrag.startX;
        if (!this.thumbnailDrag.moved && Math.abs(distance) <= THUMBNAIL_DRAG_THRESHOLD) return;

        if (!this.thumbnailDrag.moved) {
          this.thumbnailDrag.moved = true;
          this.thumbnailDrag.startX = event.clientX;
          this.thumbnailDrag.startScrollLeft = this.thumbnailRail.scrollLeft;
          this.thumbnailDrag.velocityHistory = [{ x: event.clientX, time: event.timeStamp }];
          this.thumbnailRail.classList.add('is-dragging');
          this.thumbnailRail.setPointerCapture?.(event.pointerId);
          event.preventDefault();
          return;
        }

        this.thumbnailRail.scrollLeft = this.thumbnailDrag.startScrollLeft - distance;
        this.thumbnailDrag.velocityHistory.push({ x: event.clientX, time: event.timeStamp });
        const cutoff = event.timeStamp - THUMBNAIL_VELOCITY_WINDOW;
        this.thumbnailDrag.velocityHistory = this.thumbnailDrag.velocityHistory.filter(({ time }) => time >= cutoff);
        event.preventDefault();
      }

      onThumbnailPointerUp(event) {
        this.finishThumbnailDrag(event, true);
      }

      onThumbnailPointerCancel(event) {
        this.finishThumbnailDrag(event, false);
      }

      onThumbnailPointerCaptureLost(event) {
        this.finishThumbnailDrag(event, false);
      }

      finishThumbnailDrag(event, suppressClick) {
        const drag = this.thumbnailDrag;
        if (!drag || (event && event.pointerId !== drag.pointerId)) return;
        if (!suppressClick) this.stopThumbnailInertia();

        this.thumbnailDrag = null;
        if (this.thumbnailRail?.hasPointerCapture?.(drag.pointerId)) {
          this.thumbnailRail.releasePointerCapture(drag.pointerId);
        }

        if (!drag.moved || !suppressClick) {
          this.thumbnailRail?.classList.remove('is-dragging');
          return;
        }
        this.suppressThumbnailClick = true;
        this.thumbnailClickReset = window.setTimeout(() => this.clearThumbnailClickSuppression());
        this.startThumbnailInertia(this.getThumbnailReleaseVelocity(drag));
      }

      getThumbnailReleaseVelocity(drag) {
        const first = drag.velocityHistory[0];
        const last = drag.velocityHistory.at(-1);
        const elapsed = last?.time - first?.time;
        if (!first || !last || elapsed <= 0) return 0;

        return Math.max(
          -THUMBNAIL_MAX_VELOCITY,
          Math.min(THUMBNAIL_MAX_VELOCITY, (-(last.x - first.x) / elapsed) * 1000)
        );
      }

      startThumbnailInertia(velocity) {
        if (Math.abs(velocity) < THUMBNAIL_STOP_VELOCITY || !window.requestAnimationFrame) {
          this.thumbnailRail?.classList.remove('is-dragging');
          return;
        }

        this.thumbnailInertiaVelocity = velocity;
        this.thumbnailInertiaTime = null;
        this.thumbnailInertiaFrame = window.requestAnimationFrame(this.stepThumbnailInertia);
      }

      stepThumbnailInertia(time) {
        this.thumbnailInertiaFrame = null;
        if (!this.isConnected || !this.thumbnailRail) {
          this.stopThumbnailInertia();
          return;
        }
        if (this.thumbnailInertiaTime === null) {
          this.thumbnailInertiaTime = time;
          this.thumbnailInertiaFrame = window.requestAnimationFrame(this.stepThumbnailInertia);
          return;
        }

        const elapsed = (time - this.thumbnailInertiaTime) / 1000;
        this.thumbnailInertiaTime = time;
        if (elapsed <= 0) {
          this.thumbnailInertiaFrame = window.requestAnimationFrame(this.stepThumbnailInertia);
          return;
        }

        this.thumbnailInertiaVelocity *= Math.exp(-THUMBNAIL_DECAY * elapsed);
        if (Math.abs(this.thumbnailInertiaVelocity) < THUMBNAIL_STOP_VELOCITY) {
          this.stopThumbnailInertia();
          return;
        }

        const start = this.thumbnailRail.scrollLeft;
        const target = start + this.thumbnailInertiaVelocity * elapsed;
        this.thumbnailRail.scrollLeft = target;
        if (target !== start && this.thumbnailRail.scrollLeft === start) {
          this.stopThumbnailInertia();
          return;
        }

        this.thumbnailInertiaFrame = window.requestAnimationFrame(this.stepThumbnailInertia);
      }

      stopThumbnailInertia() {
        if (this.thumbnailInertiaFrame != null) window.cancelAnimationFrame(this.thumbnailInertiaFrame);
        this.thumbnailInertiaFrame = null;
        this.thumbnailInertiaTime = null;
        this.thumbnailInertiaVelocity = 0;
        this.thumbnailRail?.classList.remove('is-dragging');
      }

      onThumbnailClick(event) {
        if (!this.suppressThumbnailClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.clearThumbnailClickSuppression();
      }

      preventThumbnailNativeDrag(event) {
        event.preventDefault();
      }

      clearThumbnailClickSuppression() {
        window.clearTimeout(this.thumbnailClickReset);
        this.thumbnailClickReset = null;
        this.suppressThumbnailClick = false;
      }

      updateThumbnailPageSize() {
        const activePageSize = this.thumbnailPageQueries.find(({ media }) => media.matches)?.step || 2;
        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => (button.dataset.step = activePageSize));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const request = (this.activeMediaRequest || 0) + 1;
        this.activeMediaRequest = request;
        this.clearPreviewLoading();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;
        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        const image = activeMedia.querySelector('.product__modal-opener--image img');
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const transitionViewer = this.freezePreviewSize(reducedMotion);
        if (!image) {
          this.finishActiveMedia(activeMedia, activeThumbnail, request);
          if (transitionViewer) transitionViewer.style.blockSize = 'auto';
          return;
        }

        const loadingState = this.showPreviewSkeleton(activeMedia, image);
        const ready = this.waitForImage(loadingState);
        this.swapActiveMedia(activeMedia, activeThumbnail, request, transitionViewer);
        ready.then(() => {
          if (request !== this.activeMediaRequest || !this.isConnected || this.previewLoadingState !== loadingState)
            return;
          this.clearPreviewLoading(loadingState, false);
        });
      }

      showPreviewSkeleton(media, image) {
        const container = image.closest('.product-media-container');
        const width = Number(image.getAttribute('width'));
        const height = Number(image.getAttribute('height'));
        const configuredRatio = Number.parseFloat(
          container ? window.getComputedStyle(container).getPropertyValue('--preview-ratio') : ''
        );
        const ratio = configuredRatio || (width > 0 && height > 0 ? width / height : NaN);
        const fallbackHeight = this.elements.viewer.getBoundingClientRect().height;
        const state = { container, image, media };
        this.previewLoadingState = state;
        media.classList.add('is-preview-loading');

        if (container && Number.isFinite(ratio) && ratio > 0) {
          media.classList.add('is-preview-loading--ratio');
          container.style.setProperty('--preview-loading-ratio', String(ratio));
        } else if (container && Number.isFinite(fallbackHeight) && fallbackHeight > 0) {
          media.classList.add('is-preview-loading--height');
          container.style.setProperty('--preview-loading-height', `${fallbackHeight}px`);
        }

        return state;
      }

      waitForImage(state) {
        const { image } = state;
        image.loading = 'eager';
        const decode = () =>
          typeof image.decode === 'function' ? Promise.resolve(image.decode()).catch(() => {}) : Promise.resolve();
        if (image.complete) return decode();

        return new Promise((resolve) => {
          let settled = false;
          const cleanup = () => {
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
            if (state.cancelReadiness === cancel) state.cancelReadiness = null;
          };
          const finish = (shouldDecode) => {
            if (settled) return;
            settled = true;
            cleanup();
            (shouldDecode ? decode() : Promise.resolve()).then(resolve);
          };
          const onLoad = () => finish(true);
          const onError = () => finish(false);
          const cancel = () => finish(false);
          state.cancelReadiness = cancel;
          image.addEventListener('load', onLoad, { once: true });
          image.addEventListener('error', onError, { once: true });
          if (image.complete) finish(true);
          else if (image.src) image.src = image.src;
        });
      }

      clearPreviewLoading(state = this.previewLoadingState, cancel = true) {
        if (!state) return;
        if (cancel) state.cancelReadiness?.();
        state.media.classList.remove('is-preview-loading', 'is-preview-loading--ratio', 'is-preview-loading--height');
        state.container?.style.removeProperty('--preview-loading-ratio');
        state.container?.style.removeProperty('--preview-loading-height');
        if (this.previewLoadingState === state) this.previewLoadingState = null;
      }

      swapActiveMedia(activeMedia, activeThumbnail, request, transitionViewer) {
        this.finishActiveMedia(activeMedia, activeThumbnail, request);
        if (transitionViewer) transitionViewer.style.blockSize = 'auto';
      }

      freezePreviewSize(reducedMotion) {
        const viewer = this.elements.viewer;
        const canTransition =
          !reducedMotion &&
          this.mql.matches &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          viewer.matches('slider-component.main-image-block') &&
          window.CSS?.supports?.('interpolate-size: allow-keywords');
        if (!canTransition) {
          viewer.style.removeProperty('block-size');
          return null;
        }

        const blockSize = viewer.getBoundingClientRect().height;
        if (!Number.isFinite(blockSize) || blockSize <= 0) {
          viewer.style.removeProperty('block-size');
          return null;
        }
        const transition = viewer.style.transition;
        viewer.style.transition = 'none';
        viewer.style.blockSize = `${blockSize}px`;
        void viewer.offsetHeight;
        viewer.style.transition = transition;
        void viewer.offsetHeight;
        return viewer;
      }

      finishActiveMedia(activeMedia, activeThumbnail, request) {
        if (request !== this.activeMediaRequest) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        window.setTimeout(() => {
          if (request !== this.activeMediaRequest) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
